import { CancelIntentAction, CheckInResponseAction } from '@foodfluence/shared';
import { makeClientBackendRequest } from './apiUtils';

export interface ProcessFAQMessageResponse {
  success: boolean;
  matched?: boolean;
  questionId?: string;
  response?: string;
}

export type PreVisitCheckInResponseResult =
  | 'confirmed'
  | 'canceled'
  | 'navigateToReschedule'
  | 'navigateToContentSubmission'
  | 'alertRaised'
  /** Recorded and answered by the brand in the thread — no toast needed. */
  | 'acknowledged'
  | 'noop';

export interface RecordPreVisitCheckInResponse {
  success: boolean;
  result: PreVisitCheckInResponseResult;
}

/**
 * Record the creator's response to a pre-visit check-in (quick-reply chip tap).
 * The dashboard updates check-in state, cancels pending follow-up tasks, and runs
 * the cancel / reschedule-alert side effects; the returned `result` tells the UI
 * whether to send the creator into the reschedule flow.
 */
export const recordPreVisitCheckInResponse = async (
  collabId: string,
  action: CheckInResponseAction
): Promise<RecordPreVisitCheckInResponse> => {
  return makeClientBackendRequest({
    method: 'POST',
    endpoint: '/collab-check-in/pre-visit-response',
    data: { collabId, action }
  });
};

/**
 * Record the creator's answer to a Shopify "did our package arrive?" check-in ("Yes, got it!" /
 * "Not yet" / "Something's wrong"). The dashboard marks the order received, has the brand reply,
 * or alerts the team, and stops the follow-up messages.
 */
export const recordShopifyArrivalCheckInResponse = async (
  collabId: string,
  action: CheckInResponseAction
): Promise<RecordPreVisitCheckInResponse> => {
  return makeClientBackendRequest({
    method: 'POST',
    endpoint: '/collab-check-in/shopify-arrival-response',
    data: { collabId, action }
  });
};

export type CancelIntentResult = 'canceled' | 'kept' | 'undone' | 'rescheduling' | 'noop';

export interface RecordCancelIntentResponse {
  success: boolean;
  result: CancelIntentResult;
}

/**
 * Record the creator's answer to the inbox cancel agent's confirmation prompt.
 *
 * The dashboard owns the whole sequence on a "Yes, cancel": eligibility re-check, the collab
 * status write, the brand notification, reminder teardown and the wallet release. On "Undo" it
 * runs the reverse through the shared uncancel path, re-reserving the wallet. A `noop` result
 * means the prompt was already answered, has gone stale, or the undo window has closed.
 */
export const recordCancelIntentResponse = async (
  collabId: string,
  action: CancelIntentAction,
  /** Id of the message the chip was tapped on — the dashboard rejects a superseded prompt. */
  promptMessageId?: string
): Promise<RecordCancelIntentResponse> => {
  return makeClientBackendRequest({
    method: 'POST',
    endpoint: '/inbox/cancel-intent-response',
    data: { collabId, action, promptMessageId }
  });
};

export const processFAQMessage = async (
  collabId: string,
  messageText: string,
  messageId: string
): Promise<ProcessFAQMessageResponse> => {
  return makeClientBackendRequest({
    method: 'POST',
    endpoint: '/inbox/process-faq',
    data: {
      collabId,
      messageText,
      messageId
    }
  });
};
